/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useMemo, useState, useEffect } from 'react';
import en from '../assets/i18n/en.json';
import es from '../assets/i18n/es.json';
import { getFromStorage, saveToStorage } from '@/utils/storage';
import { isElectron } from '@/utils/platformUtils';

interface LanguageProviderContextProps {
  userLanguage: string;
  setUserLanguage: (lang: string) => void;
  translator: (key: string, opts?: Record<string,string> ) => string;
  dictionary: Record<string, any>;
}

export const LanguageProviderContext =
  createContext<LanguageProviderContextProps>({
    userLanguage: 'en',
    setUserLanguage: (lang: string) => {},
    translator: ()=>'',
    dictionary: en,
  });

const LangProvider: React.FC<any> = (props) => {
  const detectedLanguage = navigator.language?.startsWith('es') ? 'es' : 'en';
  const storedLanguage = getFromStorage<string>('language');
  const [userLanguage, setUserLanguage] = useState<string | null>(
    detectedLanguage || storedLanguage,
  );
  useEffect(() => {
    if (!userLanguage || !['en', 'es'].includes(userLanguage)) return;
    if (isElectron() && window.electronAPI?.syncLanguage) {
      window.electronAPI.syncLanguage(userLanguage);
    }
  }, [userLanguage]);
  const updatedDictionary = useMemo(() => {
    switch (userLanguage) {
      case 'en':
        saveToStorage('language', 'en');
        return en;
      case 'es':
        saveToStorage('language', 'es');
        return es;
      default:
        return es;
    }
  }, [userLanguage]);

  const getValueByPath = (obj: Record<string, any>, path: string, defaultValue?: any) => {
    return path
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj) ?? defaultValue;
  };

  const translator = (key: string, opts?: Record<string, string>) => {
    const translatedValue: string = getValueByPath(updatedDictionary, key, undefined);

    if (opts && translatedValue) {
      return translatedValue.replaceAll(/\{{(\w+)\}}/g, (_, k) => opts[k] || '');
    }
      return !translatedValue ? key : translatedValue;
  };

  return (
    <LanguageProviderContext.Provider
      value={{
        userLanguage,
        setUserLanguage: setUserLanguage,
        dictionary: updatedDictionary,
        translator:translator,
      }}>
      {props.children}
    </LanguageProviderContext.Provider>
  );
};

export const supportedLangs: Array<string> = ['es', 'en'];

export default LangProvider;
